/**
 * What every part of the entry page shares: the entry it shows, the ad origin, the conversation
 * id, the open tab of the account panel and the measurement. The sign-up and login cards appear
 * in three places (side panel, inside the chat, the phone overlay) and must report the same way.
 */
import { createContext, useContext } from 'react';
import type { Attribution } from 'features/engagement/signup/attribution';
import { sendSignupEvent, type EntryStep, type SignupErrorField, type SignupStep } from 'features/engagement/signup/web-events';
import type { AuthTab, ChatEntry } from './entry';

export interface EntryContextValue {
  entry: ChatEntry;
  attribution?: Attribution;
  /** The chat conversation, so sign-up and login can record how it ended. */
  sessionId: string | null;
  tab: AuthTab;
  /** Switch or open the account panel; `cta` says from where (rail, topbar, card, sticky, chat). */
  openAuth: (tab: AuthTab, cta: string) => void;
  /** A Google sign-in that came back without an account (google-callback.tsx). */
  googleNoAccount: boolean;
}

const EntryContext = createContext<EntryContextValue>({
  entry: 'direkt',
  sessionId: null,
  tab: 'signup',
  openAuth: () => undefined,
  googleNoAccount: false,
});

export const EntryProvider = EntryContext.Provider;

export const useEntry = () => useContext(EntryContext);

/** Sends one step of the entry page, tagged with the entry it showed. */
export const trackEntry = (
  ctx: Pick<EntryContextValue, 'entry' | 'attribution'>,
  type: SignupStep | EntryStep,
  field?: SignupErrorField | string,
  detail: { depth?: number; cta?: string } = {},
) => sendSignupEvent(type, ctx.attribution, field, { section: ctx.entry, ...detail });
